/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { cloneDeep as _cloneDeep, get as _get } from 'lodash';
import { IPartnerThemeSettings, IViewPort } from '../../interfaces';

/**
 * Constructs the viewport information for an image using the current module and theme settings
 * Also constructs the query string for the image resizer
 *
 * @param moduleId The id of the module
 * @param moduleConfig The config object for the module
 * @param pathToField The path to the image the field
 * @param quality The image quality selected
 * @param themeSettings The current theme settings
 * @param focalRegion The image focal region settings
 * @param cropRegions The image crop region per layout/viewport settings
 * @param format The image format
 */
export const constructViewportInformationForImage = (
    moduleName: string | undefined,
    moduleLayout: string | undefined,
    propertyName: string | undefined,
    themeSettings: IPartnerThemeSettings | undefined
): unknown => {
    if (!moduleName) {
        return null;
    }
    const imageSettings = {
        viewports: {
            xs: {
                width: 800,
                height: 600
            },
            sm: {
                width: 1200,
                height: 900
            },
            md: {
                width: 1600,
                height: 900
            },
            lg: {
                width: 1600,
                height: 700
            },
            xl: {
                width: 1600,
                height: 700
            }
        }
    };

    // Drill down into the theme settings object for the current module, current module layout and image property name
    if (themeSettings && themeSettings.modules && moduleLayout && propertyName) {
        const themeSettingsForModule = themeSettings.modules[moduleName];
        if (themeSettingsForModule) {
            const themeSettingsForLayout = themeSettingsForModule.properties[moduleLayout];
            if (themeSettingsForLayout) {
                const imageViewportProperties = _get(themeSettingsForLayout.properties, [propertyName], { properties: imageSettings })
                    .properties;
                // Clone the image viewport properties from theme settings
                // so that the proxy traps do not affect the global themesettings
                imageSettings.viewports = _cloneDeep(imageViewportProperties);
            }
        }
    }
    // resolve .h and .w propety from .height and .width of viewports.
    // need to keep those ts-ignore because both .w and .width could be passed to viewPort.
    Object.keys(imageSettings.viewports).forEach((key2: string) => {
        const key = <keyof IViewPort>key2;
        if (imageSettings.viewports[key].height) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (<any>imageSettings.viewports[key]).h = (<any>imageSettings.viewports[key]).h || imageSettings.viewports[key].height;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            delete (<any>imageSettings.viewports[key]).height;
        }
        if (imageSettings.viewports[key].width) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (<any>imageSettings.viewports[key]).w = (<any>imageSettings.viewports[key]).w || imageSettings.viewports[key].width;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            delete (<any>imageSettings.viewports[key]).height;
        }
    });
    return imageSettings.viewports;
};
